import { watch } from 'vue';
import type { PersistFieldConfig } from '~/stores/pinia-augment.d.ts';
import type { QueryChanges } from '../types/urlQuery';
import { useUrlQuery } from '../composables/urlQuery';

const IS_LOCAL_ENV = process.env.NUXT_PUBLIC_IS_LOCAL === 'true';

function logPinia(...args: any[]): void {
  if (IS_LOCAL_ENV) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

export default defineNuxtPlugin({
  name: 'pinia-persistence',

  setup(nuxtApp) {
    const pinia = nuxtApp.$pinia as any;

    if (!pinia) {
      // eslint-disable-next-line no-console
      console.warn('[PINIA]: pinia instance is unavailable, skipping persistence plugin registration');
      return;
    }

    logPinia('[PINIA]: Registering persistence plugin');
    const router = useRouter();
    const { saveQuery } = useUrlQuery();

    pinia.use(({ store, options }: any) => {
      const persist: Record<string, PersistFieldConfig> | undefined = options.persist;

      if (!persist) return;

      const { query } = router.currentRoute.value;

      for (const [field, config] of Object.entries(persist)) {
        const keys: string[] = [config.query ?? []].flat();
        const fromQuery = keys.map(key => query[key]).find(value => typeof value === 'string' && value);
        const restored = fromQuery ?? readLocalStorage(config.local);

        if (!config.readOnly) {
          watch(
            () => store[field],
            (value: any) => persistValue(config, value, saveQuery),
            { flush: 'sync' },
          );
        }

        if (restored !== undefined) store[field] = restored;
      }
    });
  },
});

function persistValue(
  config: PersistFieldConfig,
  value: any,
  saveQuery: (changes: QueryChanges) => void,
): void {
  const [key] = [config.query ?? []].flat();

  if (key) saveQuery({ [key]: toQueryValue(value) ?? undefined });

  writeLocalStorage(config.local, value);
}

function readLocalStorage(key: string | undefined): unknown {
  if (!key) return undefined;

  try {
    const raw = localStorage.getItem(key);

    return raw == null || raw === '' ? undefined : parseStorageValue(raw);
  } catch (error) {
    logPinia('[PINIA]: localStorage read failed', key, error);
    return undefined;
  }
}

function writeLocalStorage(key: string | undefined, value: any): void {
  if (!key) return;

  try {
    if (value == null || value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, serializeStorageValue(value));
  } catch (error) {
    logPinia('[PINIA]: localStorage write failed', key, error);
  }
}

/**
 * Parses a raw localStorage string consistently with the $locally plugin
 * (local-storage.ts) to avoid mismatches when both coexist.
 */
function parseStorageValue(raw: string): any {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function serializeStorageValue(value: any): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function toQueryValue(value: any): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return null;
  return String(value);
}
